import { useCallback, useEffect, useState } from "react";
import {
  ImageOverrideMeta,
  getImageOverride,
  setImageOverride,
  removeImageOverride,
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_SIZE_BYTES,
} from "./imageStore";

interface SelectFileResult {
  ok: boolean;
  error?: string;
}

// Shared by the public landing (read-only: just the resolved `src`) and by
// the /content-admin image controls (full read/write/clear). Reading the
// same localStorage-backed store keeps both in sync — no duplicate source.
export function useResourceImage(resourceKey: string, fallbackSrc: string) {
  const [meta, setMeta] = useState<ImageOverrideMeta | null>(() => getImageOverride(resourceKey));

  useEffect(() => {
    setMeta(getImageOverride(resourceKey));
  }, [resourceKey]);

  const selectFile = useCallback(
    (file: File): Promise<SelectFileResult> => {
      return new Promise((resolve) => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          resolve({ ok: false, error: "Formato no permitido. Usa JPG, JPEG, PNG o WEBP." });
          return;
        }
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
          resolve({ ok: false, error: "El archivo supera el límite de 5 MB." });
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          const newMeta: ImageOverrideMeta = {
            dataUrl,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            selectedAt: new Date().toISOString(),
          };
          try {
            setImageOverride(resourceKey, newMeta);
            setMeta(newMeta);
            resolve({ ok: true });
          } catch {
            resolve({
              ok: false,
              error: "No se pudo guardar la imagen: espacio de almacenamiento del navegador insuficiente.",
            });
          }
        };
        reader.onerror = () => resolve({ ok: false, error: "No se pudo leer el archivo." });
        reader.readAsDataURL(file);
      });
    },
    [resourceKey],
  );

  const clear = useCallback(() => {
    removeImageOverride(resourceKey);
    setMeta(null);
  }, [resourceKey]);

  return {
    src: meta?.dataUrl || fallbackSrc,
    meta,
    isOverridden: !!meta,
    selectFile,
    clear,
  };
}
